import { useCallback, useEffect, useMemo, useState } from 'react'

import { type SmallIcon } from '@factorialco/deprecated-design-system'
import {
  CompetenciesFinderCreateFragment,
  CompetenciesFinderFactorialCompetencyFragment,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

export interface CompetencyForm {
  name: string
  description?: string | null
  groupId?: string
  groupName?: string
}

export type CompetencyConfig = {
  id: number
  readonly?: boolean
  ui?: {
    icon?: SmallIcon
    tooltip?: {
      title?: string
      description?: string
    }
  }
}

export interface Competency {
  id: number
  name: string
  description?: string | null
  factorialCompetencyUuid?: string | null
}

type Source = 'suggestions' | 'combobox' | 'originalFromTarget' | 'createNewForm'
export interface SelectedCompetency {
  identifier: string
  name: string
  description?: string | null
  groupId?: string
  groupName?: string
  factorialCompetencyUuid?: string | null
  type: 'factorial_competency' | 'company_competency' | 'new_competency'
  source?: Source
  readonly?: boolean
  ui?: CompetencyConfig['ui']
  error?: string
}
type CompetencyType = CompetenciesFinderFactorialCompetencyFragment | Competency | CompetencyForm

const isFactorialCompetency = (
  competency: CompetencyType
): competency is CompetenciesFinderFactorialCompetencyFragment => {
  return 'uuid' in competency && typeof competency.uuid === 'string'
}

const isCompetency = (competency: CompetencyType): competency is Competency => {
  return 'id' in competency && typeof competency.id === 'number'
}

const isCompetencyForm = (competency: CompetencyType): competency is CompetencyForm => {
  return !isFactorialCompetency(competency) && !isCompetency(competency)
}

export const toSelectedCompetency = (
  competency: CompetencyType,
  source: Source,
  readonly = false,
  ui?: CompetencyConfig['ui']
): SelectedCompetency => {
  if (isFactorialCompetency(competency)) {
    return {
      identifier: competency.uuid,
      name: competency.name,
      description: competency.description,
      type: 'factorial_competency',
      factorialCompetencyUuid: competency.uuid,
      source,
      readonly,
      ui,
    }
  }

  if (isCompetency(competency)) {
    return {
      identifier: competency.id.toString(),
      name: competency.name,
      description: competency.description,
      type: 'company_competency',
      source,
      factorialCompetencyUuid: competency.factorialCompetencyUuid,
    }
  }

  if (isCompetencyForm(competency)) {
    return {
      identifier: uuid(),
      name: competency.name,
      description: competency.description,
      type: 'new_competency',
      groupId: competency.groupId,
      groupName: competency.groupName,
      source,
    }
  }

  // This should never happen with proper TypeScript, but provides runtime safety
  throw new Error('Unknown competency type')
}

export const hasToBeCreated = (competency: SelectedCompetency) =>
  competency.type === 'new_competency'

export const getNewCompetencyInputs = (competencies: SelectedCompetency[]) => {
  return competencies.map((competency) => {
    const hasGroup = competency.groupId && competency.groupId !== 'new'
    return {
      name: competency.name,
      description: competency.description,
      factorialCompetencyUuid: competency.factorialCompetencyUuid,
      competencyGroupId: competency.groupId,
      competencyGroupName: hasGroup ? null : competency.groupName,
      options: [],
    }
  })
}

export const selectionToCompanyCompetencies = (selected: SelectedCompetency[]): Competency[] => {
  return selected
    .filter(({ type }) => type === 'company_competency')
    .map(({ identifier, name, description }) => ({
      id: parseInt(identifier),
      name,
      description,
    }))
}

type CompetencyFinderProps = {
  initialConfig: CompetencyConfig[]
  fetchedCompetencies: Competency[]
}

type CompetencyFinderReturn = {
  selectedCompetencies: SelectedCompetency[]
  initialFactorialUuids: string[]
  excludeUuidSuggestions: string[]
  syncCreated: (created: CompetenciesFinderCreateFragment[]) => SelectedCompetency[]
  addCompetency: (competency: SelectedCompetency) => void
  removeCompetency: (competency: SelectedCompetency) => void
}

export const useCompetencyFinder = ({
  initialConfig = [],
  fetchedCompetencies = [],
}: CompetencyFinderProps): CompetencyFinderReturn => {
  const [selectedCompetencies, setSelectedCompetencies] = useState<SelectedCompetency[]>([])
  const [dismissedFactorialSuggestions, setDismissedFactorialSuggestions] = useState<string[]>([])
  const [initializedVersion, setInitializedVersion] = useState<string>('')

  const dataVersion = useMemo(() => {
    if (!fetchedCompetencies.length) return ''
    return `${initialConfig
      .map((c) => c.id)
      .sort()
      .join(',')}-${fetchedCompetencies.length}`
  }, [initialConfig, fetchedCompetencies])

  useEffect(() => {
    if (fetchedCompetencies.length > 0 && dataVersion && dataVersion !== initializedVersion) {
      setInitializedVersion(dataVersion)
      const competencyConfigs = new Map(initialConfig.map((config) => [config.id, config]))
      const initial = fetchedCompetencies.map((competency) => ({
        ...toSelectedCompetency(competency, 'originalFromTarget'),
        readonly: competencyConfigs.get(competency.id)?.readonly,
        ui: competencyConfigs.get(competency.id)?.ui,
      }))

      setSelectedCompetencies(initial)
    }
  }, [fetchedCompetencies, initialConfig, dataVersion, initializedVersion])

  const addCompetency = useCallback((competency: SelectedCompetency) => {
    setSelectedCompetencies((prev) => {
      const exists = prev.some((c) => c.identifier === competency.identifier)
      if (exists) return prev
      return [...prev, competency]
    })
  }, [])

  const removeCompetency = useCallback((competency: SelectedCompetency) => {
    if (competency.readonly) {
      return
    }

    setSelectedCompetencies((prev) => prev.filter((c) => c.identifier !== competency.identifier))
    if (
      competency.source === 'suggestions' &&
      competency.type === 'factorial_competency' &&
      competency.factorialCompetencyUuid
    ) {
      setDismissedFactorialSuggestions((prev) => [
        ...prev,
        competency?.factorialCompetencyUuid || '',
      ])
    }
  }, [])

  const initialFactorialUuids = useMemo(() => {
    return fetchedCompetencies.map((c) => c.factorialCompetencyUuid).filter(Boolean)
  }, [fetchedCompetencies])

  const excludeUuidSuggestions = useMemo(() => {
    const selectedSuggestionsUuids = selectedCompetencies
      .map((c) => c.factorialCompetencyUuid)
      .filter(Boolean)
    const exclutions = new Set([...selectedSuggestionsUuids, ...dismissedFactorialSuggestions])
    return [...exclutions]
  }, [selectedCompetencies, dismissedFactorialSuggestions])

  const syncCreated = (created: CompetenciesFinderCreateFragment[]): SelectedCompetency[] => {
    const createdMap = new Map(created.map((c) => [c.name, c]))
    const selectedCompetenciesUpdated: SelectedCompetency[] = selectedCompetencies.map(
      (competency) => {
        if (!hasToBeCreated(competency)) {
          return competency
        }

        const createdCompetency = createdMap.get(competency.name)
        if (!createdCompetency) {
          return {
            ...competency,
            error: 'Could not find created competency ',
          }
        }

        return {
          identifier: createdCompetency.id.toString(),
          name: createdCompetency.name,
          description: createdCompetency.description,
          type: 'company_competency',
          source: competency.source,
          groupId: createdCompetency?.competencyGroup?.id,
          groupName: createdCompetency?.competencyGroup?.name,
          factorialCompetencyUuid: createdCompetency.factorialCompetencyUuid,
          readonly: competency.readonly,
          ui: competency.ui,
        }
      }
    )
    setSelectedCompetencies(selectedCompetenciesUpdated)
    return selectedCompetenciesUpdated
  }

  return {
    selectedCompetencies,
    initialFactorialUuids,
    excludeUuidSuggestions,
    addCompetency,
    removeCompetency,
    syncCreated,
  }
}
